/**
 * Reactive form of the acquisition-channel gate (see `config/acquisitionChannels`).
 *
 * The gate predicates resolve at call time, so a page reload always reflects the current
 * flags. This hook adds LIVE reactivity: it subscribes to the runtime `show_in_progress`
 * reveal flag (see {@link useShowInProgress}) so the host component re-renders the instant a
 * gated channel is unlocked/locked, no reload needed. Use it in any component or hook that
 * renders gated channel UI; the bare predicates from the config stay for non-React code
 * (the allocation engine, async fetch filters).
 *
 * Returns the same predicates plus `revealed` — pass `revealed` into `useMemo` / `useEffect`
 * dependency arrays so memoised/derived state recomputes when the flag flips (the predicate
 * references themselves are stable and would not trip a dependency check on their own).
 */
import React from 'react';

import { isAcquisitionChannelEnabled, isAcquisitionChannelLocked } from '../config/acquisitionChannels';
import useShowInProgress from './useShowInProgress';

export type AcquisitionChannelGate = {
  /** Whether the runtime `show_in_progress` flag is currently revealing the gated channels. */
  revealed: boolean;
  /** Whether a channel is live. Accepts any of the three key schemes (`aft` / `channelId` / `homeId`). */
  isEnabled: (key: string | undefined) => boolean;
  /** Inverse of {@link AcquisitionChannelGate.isEnabled}. */
  isLocked: (key: string | undefined) => boolean;
};

export const useAcquisitionChannelGate = (): AcquisitionChannelGate => {
  const revealed = useShowInProgress();
  // Re-key the object on `revealed` so consumers depending on the gate identity recompute on a flip.
  return React.useMemo(
    () => ({ revealed, isEnabled: isAcquisitionChannelEnabled, isLocked: isAcquisitionChannelLocked }),
    [revealed],
  );
};

export default useAcquisitionChannelGate;
